import React from 'react';
import { Route, IndexRedirect, IndexRoute } from 'react-router';
import { logCheckpointViaAjax } from 'js-utils/janus';

import Page from './Page';
import {
    BASE_URL,
    OVERVIEW_PATH,
    PRICING_PATH,
    PACKAGE_COMPARISON_PATH,
    PACKAGE_ONE_PATH,
    PACKAGE_TWO_PATH,
    PACKAGE_THREE_PATH,
    ONBOARDING_PATH,
    PACKAGE_SELECTION_PATH,
    SIGNUP_PATH,
    CHANGE_PLAN_PATH,
    CHANGE_PLAN_DOWNGRADE_PATH,
    CHANGE_PLAN_SALES_PATH,
    CHANGE_PLAN_NON_ADMIN_PATH,
    CHANGE_PLAN_UPDATE_PATH,
    CHANGE_PLAN_DOWNGRADE_CONFLICTS_PATH,
} from '../constants/main';
import {
    ConfirmPlanConnectedUpdatePage,
    ConnectedOverviewPage,
    ConnectedPricingPage,
    ConnectedPackageComparisonPage,
    ConnectedPackageOnePage,
    ConnectedPackageTwoPage,
    ConnectedPackageThreePage,
    ConnectedSignUpPage,
    ConnectedChangePlanPage,
    ConnectedSalesPage,
    ConnectedAccessPage,
    ConnectedUpdatePage,
    ConfirmPlanConnectedDowngradePage,
    ConnectedDowngradePage,
    ConnectedPackageDowngradeConflictsPage,
} from '../containers';
import {
    ConnectedOnboardingPage,
    ConnectedPackageSelectionPage,
} from '../containers/onboarding';
import {
    checkAccess,
    checkChangePlanAccess,
} from '../containers/onboarding/utils';
import { SIGNUP_PACKAGE_SELECTION_VIEW } from '../constants/analytics';
import {
    canUpdateEssentialsV2,
    isPackageProfessional,
} from '../containers/utils';

const getDowngradeRoutes = (
    isPackageDowngradeEnabled = false,
    checkChangePlanAccessOnEnter,
) => {
    if (!isPackageDowngradeEnabled) {
        return (
            <Route
                path={CHANGE_PLAN_DOWNGRADE_PATH}
                component={ConnectedDowngradePage}
                onEnter={checkChangePlanAccessOnEnter}
            />
        );
    }

    return (
        <Route path={CHANGE_PLAN_DOWNGRADE_PATH}>
            <IndexRoute
                component={ConfirmPlanConnectedDowngradePage}
                onEnter={checkChangePlanAccessOnEnter}
            />
            <Route
                path={CHANGE_PLAN_DOWNGRADE_CONFLICTS_PATH}
                component={ConnectedPackageDowngradeConflictsPage}
                onEnter={checkChangePlanAccessOnEnter}
            />
        </Route>
    );
};

const getRoutes = (pageInfo, store) => {
    const {
        env: { locale },
        assortments: { assortmentsAvailability },
        featureFlags,
    } = store.getState();

    const PageWithInfo = (props) => <Page {...pageInfo} {...props} />;

    const checkAccessOnEnter = (nextRouterState, replace) => {
        checkAccess(store, replace);
    };

    const checkChangePlanAccessOnEnter = (nextRouterState, replace) => {
        checkChangePlanAccess(store, replace);
    };

    const logSignupJanusCheckpoints = () => {
        logCheckpointViaAjax(SIGNUP_PACKAGE_SELECTION_VIEW);
    };

    const goToBaseUrl = (nextRouterState, replace) => {
        replace(`${BASE_URL}`);
    };

    const UpdatePage = featureFlags.isPackageChangeDowngradeEnabled
        ? ConfirmPlanConnectedUpdatePage
        : ConnectedUpdatePage;

    const essentialsV1UpdateRoute = canUpdateEssentialsV2({
        locale,
        assortmentsAvailability,
    }) ? (
        <Route
            path={CHANGE_PLAN_UPDATE_PATH}
            component={UpdatePage}
            onEnter={checkChangePlanAccessOnEnter}
        />
    ) : null;

    const downgradeRoute = isPackageProfessional(assortmentsAvailability)
        ? getDowngradeRoutes(
              featureFlags.isPackageChangeDowngradeEnabled,
              checkChangePlanAccessOnEnter,
          )
        : null;

    return (
        <Route path={BASE_URL}>
            <IndexRedirect to={OVERVIEW_PATH} />
            <Route component={PageWithInfo}>
                <Route path={OVERVIEW_PATH} component={ConnectedOverviewPage} />
                <Route path={PRICING_PATH} component={ConnectedPricingPage} />
                <Route
                    path={PACKAGE_COMPARISON_PATH}
                    component={ConnectedPackageComparisonPage}
                />
                <Route
                    path={PACKAGE_ONE_PATH}
                    component={ConnectedPackageOnePage}
                />
                <Route
                    path={PACKAGE_TWO_PATH}
                    component={ConnectedPackageTwoPage}
                />
                <Route
                    path={PACKAGE_THREE_PATH}
                    component={ConnectedPackageThreePage}
                />
                <Route
                    path={ONBOARDING_PATH}
                    component={ConnectedOnboardingPage}
                    onEnter={checkAccessOnEnter}
                />
                <Route
                    path={PACKAGE_SELECTION_PATH}
                    component={ConnectedPackageSelectionPage}
                />
                <Route
                    path={SIGNUP_PATH}
                    component={ConnectedSignUpPage}
                    onEnter={logSignupJanusCheckpoints}
                />
                <Route
                    path={CHANGE_PLAN_PATH}
                    component={ConnectedChangePlanPage}
                    onEnter={checkChangePlanAccessOnEnter}
                />
                <Route
                    path={CHANGE_PLAN_SALES_PATH}
                    component={ConnectedSalesPage}
                />
                <Route
                    path={CHANGE_PLAN_NON_ADMIN_PATH}
                    component={ConnectedAccessPage}
                />
                {essentialsV1UpdateRoute}
                {downgradeRoute}
            </Route>
            <Route path="*" onEnter={goToBaseUrl} />
        </Route>
    );
};

export default getRoutes;
